import { GROUP_TAB_KEYS, GroupTab } from "@/src/constants/groups/group-tabs";
import { MOCK_SUGGESTED_GROUPS } from "@/src/constants/groups/mock-groups";
import { useAuth } from "@/src/contexts/auth.context";
import { SuggestedGroup } from "@/src/models/group.model";
import { useGetAllMyMemberships, useGetGroups, useGetLatestGroups, useGetMyMembership } from "@/src/services/api/groups/groups.hooks";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useWindowDimensions } from "react-native";

export const useGroups = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const { user } = useAuth();

  // Search
  const [searchText, setSearchText] = useState("");

  // Tabs
  const [activeTab, setActiveTab] = useState<GroupTab>(GroupTab.JOINED_GROUPS);
  const tabs = GROUP_TAB_KEYS.map((key) => t(key));
  const handleTabChange = (label: string) => {
    const key = GROUP_TAB_KEYS.find((tab_key) => t(tab_key) === label);
    if (key) setActiveTab(key);
  };

  // Toast
  const { success } = useLocalSearchParams<{ success?: string }>();
  const [toast, setToast] = useState<{
    text1: string;
    type: "success" | "error";
  } | null>(null);

  useEffect(() => {
    if (success === "create_true") {
      setToast({ text1: t("LABEL_GROUP_CREATED"), type: "success" });
    } else if (success === "update_true") {
      setToast({ text1: t("LABEL_GROUP_UPDATED"), type: "success" });
    }
  }, [success]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 1500);
    return () => clearTimeout(timer);
  }, [toast]);

  // Groups
  const {
    data,
    isPending,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetGroups();
  const { data: memberships } = useGetAllMyMemberships(user?.itemId as string);
  const allGroups =
    data?.pages.flatMap((page) => page?.getgroups?.items ?? []) ?? [];
  const filtered = allGroups.filter((group) => {
    const matchesSearch = group.name?.toLowerCase().includes(searchText.toLowerCase());

    let matchesTab = true;
    if (activeTab === GroupTab.MY_GROUPS) {
      matchesTab = group.CreatedBy === user?.itemId;
    } else if (activeTab === GroupTab.JOINED_GROUPS) {
      matchesTab = memberships?.some((membership) => membership.groupId === group.ItemId) ?? false;
    } else {
      matchesTab = true;
    }
    return matchesSearch && matchesTab;
  });
  const loadMore = () => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  };

  // Latest groups (most recently created) for the right-panel sidebar
  const { data: latestGroups = [] } = useGetLatestGroups(5);

  // Navigation
  const handleViewGroup = (id: string) => router.push(`/groups/${id}`);

  // Layout
  const { width } = useWindowDimensions();
  const numColumns = width > 600 ? 2 : 1;

  return {
    searchText,
    setSearchText,

    tabs,
    activeTab,
    handleTabChange,

    toast,
    setToast,

    filtered,
    isPending,
    error,
    isFetchingNextPage,
    loadMore,

    latestGroups,
    handleViewGroup,

    numColumns,
  };
};
